import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/analytics";

import Bugsnag from "@bugsnag/js";

import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";

import EditOrganization from "./admin/EditOrganization";
import EditQuestions from "./admin/EditQuestions";

import UserData from "../../../data/UserData";

import EditLabels from "./admin/EditLabels";
import EditProgressPhotos from "./admin/EditProgressPhotos";
import useEvents from "../../../hooks/useEvents";
import PageHeader from "../../../components/PageHeader";
import StandardButton from "../../../components/StandardButton";
import { AdminDraftProvider, useAdminDraftOptional } from "../../../context/AdminDraftContext";
import useEntitlements from "../../../hooks/useEntitlements";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      aria-hidden={value !== index}
      style={{ display: value === index ? "block" : "none" }}
      {...other}
    >
      <Box p={3}>{children}</Box>
    </div>
  );
}

function AdminPublishToolbar() {
  const admin = useAdminDraftOptional();
  const confirm = useConfirm();
  const [draftSavedToastOpen, setDraftSavedToastOpen] = React.useState(false);
  const prevAutosaveTickRef = React.useRef(0);

  React.useEffect(() => {
    if (!admin) return;
    const t = admin.draftAutosaveTick ?? 0;
    if (t <= prevAutosaveTickRef.current) return;
    prevAutosaveTickRef.current = t;
    if (t > 0) {
      setDraftSavedToastOpen(true);
    }
  }, [admin, admin?.draftAutosaveTick]);

  if (!admin) return null;

  if (!admin.liveOrg) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 48,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  const handleRevert = async () => {
    try {
      await confirm({
        title: "Discard admin draft?",
        description:
          "This removes the saved draft from the server and restores the published (live) settings everywhere in Admin. Unpublished changes will be lost.",
        confirmationText: "Revert draft",
        cancellationText: "Cancel",
        confirmationButtonProps: { color: "warning" },
      });
    } catch {
      return;
    }
    await admin.revertDraft();
  };

  const statusLine = admin.hasDraftContent ? (
    <Tooltip
      title="Draft autosaves and returns after refresh. Publish to apply live, or Revert to discard."
      placement="bottom-start"
      enterDelay={400}
    >
      <Typography
        variant="body2"
        component="span"
        sx={{
          color: "warning.dark",
          fontWeight: 600,
          lineHeight: 1.35,
          cursor: "default",
          whiteSpace: { xs: "normal", sm: "nowrap" },
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        Draft mode — not yet live · Publish or revert when ready
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
      Published — viewing live settings
    </Typography>
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.25, sm: 2 },
          pb: 1.5,
          pt: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          {admin.hasDraftContent ? (
            <WarningAmberIcon sx={{ color: "warning.main", fontSize: 20, flexShrink: 0 }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 20, flexShrink: 0 }} />
          )}
          {statusLine}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-end", sm: "flex-start" },
          }}
        >
          <StandardButton
            variant="secondary"
            disabled={!admin.hasDraftContent || admin.publishing || admin.reverting}
            onClick={handleRevert}
          >
            {admin.reverting ? "Reverting…" : "Revert draft"}
          </StandardButton>
          <StandardButton
            variant="primary"
            disabled={!admin.hasDraftContent || admin.publishing || admin.reverting}
            onClick={() => admin.publishAll()}
          >
            {admin.publishing ? "Publishing…" : "Publish all changes"}
          </StandardButton>
        </Box>
      </Box>
      <Snackbar
        open={draftSavedToastOpen}
        autoHideDuration={3200}
        onClose={() => setDraftSavedToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setDraftSavedToastOpen(false)}
          sx={{ width: "100%" }}
        >
          Draft saved
        </Alert>
      </Snackbar>
    </>
  );
}

function AdminPageInner() {
  const uid = localStorage.getItem("uid");
  const navigate = useNavigate();
  const adminDraft = useAdminDraftOptional();
  const { hasFeature } = useEntitlements();
  const canAccessApi = hasFeature("api_access");

  const tabIndexValue = localStorage.getItem("adminPage_TabIndex");
  const events = useEvents();
  const [tabIndex, setTabIndex] = React.useState(() => {
    const raw = tabIndexValue ? parseInt(tabIndexValue, 10) : 0;
    // Integration tab removed; clamp legacy saved index 5 → 0
    if (Number.isNaN(raw) || raw < 0 || raw > 4) return 0;
    return raw;
  });

  const userRef = React.useRef();

  React.useEffect(() => {
    return () => {
      if (navigate.action === "POP") {
        navigate("/home");
      }
    };
  }, [navigate]);

  React.useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Admin Page",
      page_location: "/admin",
    });
  }, []);

  React.useEffect(() => {
    async function getData() {
      let userData;

      try {
        userData = await UserData.getUserData(uid);

        if (!userData) {
          navigate("/home");
          return;
        }
      } catch (error) {
        Bugsnag.notify(error);
      }

      try {
        userRef.current = userData;
      } catch (error) {
        Bugsnag.notify(error);

        localStorage.removeItem("uid");
        localStorage.removeItem("email");
        localStorage.removeItem("organizationId");
        await firebase.auth().signOut();
        navigate("/home");
      }
    }
    getData();
  }, [uid, navigate]);

  const adminReady = !!adminDraft?.liveOrg;

  const handleChange = (event, newValue) => {
    const tabChangeEvent = {
      pageName: "admin",
      tabName: newValue,
      lastTab: tabIndex,
    };
    events.logTabSwitch(tabChangeEvent);
    setTabIndex(newValue);
    localStorage.setItem("adminPage_TabIndex", newValue);
  };

  return (
    <>
      <PageHeader
        title="Admin"
        subtitle={
          !adminReady
            ? "Loading organization settings…"
            : adminDraft?.hasDraftContent
              ? "Draft in progress — publish or revert when ready"
              : "System administration and organization settings (published live)"
        }
      />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Tabs value={tabIndex} onChange={handleChange}>
          <Tab label="General"></Tab>
          <Tab label="Report Questions"></Tab>
          <Tab label="Daily Questions"></Tab>
          <Tab label="Progress Photos"></Tab>
          <Tab label="Labels"></Tab>
        </Tabs>

        <AdminPublishToolbar />

        {!adminReady ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabIndex} index={0}>
              <EditOrganization />
            </TabPanel>

            <TabPanel value={tabIndex} index={1}>
              <EditQuestions type="weekly" />
            </TabPanel>
            <TabPanel value={tabIndex} index={2}>
              <EditQuestions type="daily" />
            </TabPanel>
            <TabPanel value={tabIndex} index={3}>
              <EditProgressPhotos />
            </TabPanel>

            <TabPanel value={tabIndex} index={4}>
              <EditLabels />
            </TabPanel>
          </>
        )}

        {canAccessApi ? (
        <Box
          sx={{
            flexShrink: 0,
            mt: "auto",
            pt: 2,
            pb: 1.5,
            px: 3,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Link
            component={RouterLink}
            to="/home/developers"
            variant="caption"
            color="text.secondary"
            underline="hover"
            sx={{ opacity: 0.72 }}
          >
            API keys & integrations
          </Link>
        </Box>
        ) : null}
      </Box>
    </>
  );
}

const AdminPage = () => {
  const uid = localStorage.getItem("uid");
  if (!uid) {
    return null;
  }
  return (
    <AdminDraftProvider uid={uid}>
      <AdminPageInner />
    </AdminDraftProvider>
  );
};

export default AdminPage;
